// @ts-nocheck
import React, { useRef, useState } from 'react';

export interface AutoDeleteSettings {
  enabled: boolean;
  target: 'unread' | 'all';
  olderThanDays: number;
}

interface ReaderSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExportOpml: () => void;
  onImportOpml: (opmlContent: string) => Promise<any>;
  autoDeleteSettings?: AutoDeleteSettings;
  onUpdateAutoDeleteSettings?: (settings: AutoDeleteSettings) => void;
  onRunAutoDeleteNow?: () => Promise<{ count: number }>;
}

export default function ReaderSettingsModal({
  isOpen,
  onClose,
  onExportOpml,
  onImportOpml,
  autoDeleteSettings = { enabled: false, target: 'unread', olderThanDays: 3 },
  onUpdateAutoDeleteSettings,
  onRunAutoDeleteNow,
}: ReaderSettingsModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importStatus, setImportStatus] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  const [cleanStatus, setCleanStatus] = useState<string | null>(null);
  const [isCleaning, setIsCleaning] = useState(false);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    setIsImporting(true);
    setImportStatus('Reading OPML file…');

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const content = event.target?.result as string;
        setImportStatus('Importing feeds…');
        const result = await onImportOpml(content);
        setImportStatus(
          `Imported ${result.imported || 0} feeds (${result.existing || 0} were already subscribed).`
        );
      } catch (err: any) {
        setImportStatus(`Import failed: ${err.message}`);
      } finally {
        setIsImporting(false);
      }
    };
    reader.readAsText(file);
  };

  const handleRunNow = async () => {
    if (!onRunAutoDeleteNow || isCleaning) return;
    setIsCleaning(true);
    setCleanStatus('Checking and pruning articles…');
    try {
      const res = await onRunAutoDeleteNow();
      if (res && res.count > 0) {
        setCleanStatus(`Successfully auto-deleted ${res.count} old article${res.count > 1 ? 's' : ''}.`);
      } else {
        setCleanStatus('No articles met the auto-delete criteria at this time.');
      }
    } catch (err: any) {
      setCleanStatus(`Auto-delete failed: ${err.message}`);
    } finally {
      setIsCleaning(false);
    }
  };

  const updateSettings = (partial: Partial<AutoDeleteSettings>) => {
    if (onUpdateAutoDeleteSettings) {
      onUpdateAutoDeleteSettings({
        ...autoDeleteSettings,
        ...partial,
      });
    }
  };

  return (
    <div className="reader-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="reader-modal-card" style={{ maxWidth: '620px' }} onClick={(e) => e.stopPropagation()}>
        <div className="reader-modal-header">
          <h3 className="reader-modal-title">Reader Settings</h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--reader-text-muted)' }}
            aria-label="Close settings"
          >
            ×
          </button>
        </div>

        <div className="reader-modal-body">
          {/* ========================================================= */}
          {/* 1. Auto-Delete Section */}
          {/* ========================================================= */}
          <div className="reader-settings-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <i className="fi fi-rr-trash" style={{ color: '#ef4444', fontSize: '1.05rem' }} />
              <h4 style={{ margin: 0, fontSize: '0.98rem', fontWeight: 600 }}>
                Auto-Delete Articles
              </h4>
            </div>
            <p style={{ margin: '0 0 12px', fontSize: '0.82rem', color: 'var(--reader-text-muted)' }}>
              Automatically prune older articles from your digital library based on age and read status.
            </p>

            {/* Step 1: Enable Auto-Delete Toggle */}
            <div className="reader-setting-toggle-row">
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--reader-text)' }}>
                  Enable Auto-Delete
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--reader-text-muted)' }}>
                  Automatically delete old articles matching your criteria
                </div>
              </div>
              <label className="reader-switch" aria-label="Toggle auto-delete">
                <input
                  type="checkbox"
                  checked={autoDeleteSettings?.enabled || false}
                  onChange={(e) => updateSettings({ enabled: e.target.checked })}
                />
                <span className="reader-switch-slider" />
              </label>
            </div>

            {/* Step 2: Custom Options (Shown when enabled) */}
            {autoDeleteSettings?.enabled && (
              <div className="reader-autodelete-options-panel">
                {/* Option 1: Which articles to auto-delete (Toggle Option) */}
                <div className="reader-form-group">
                  <label className="reader-setting-sublabel">
                    Articles to auto-delete:
                  </label>
                  <div className="reader-segmented-toggle" role="radiogroup" aria-label="Articles to auto-delete">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={autoDeleteSettings.target === 'unread'}
                      className={`reader-segment-btn ${autoDeleteSettings.target === 'unread' ? 'active' : ''}`}
                      onClick={() => updateSettings({ target: 'unread' })}
                    >
                      <i className="fi fi-rr-envelope" />
                      <span>Unopened / Unread only</span>
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={autoDeleteSettings.target === 'all'}
                      className={`reader-segment-btn ${autoDeleteSettings.target === 'all' ? 'active' : ''}`}
                      onClick={() => updateSettings({ target: 'all' })}
                    >
                      <i className="fi fi-rr-apps" />
                      <span>All articles (even read)</span>
                    </button>
                  </div>
                  <div style={{ fontSize: '0.76rem', color: 'var(--reader-text-subtle)', marginTop: '4px' }}>
                    {autoDeleteSettings.target === 'unread'
                      ? '✓ Only articles you have not opened to read will be deleted.'
                      : '✓ All articles will be deleted once old, whether read or unread.'}
                  </div>
                </div>

                {/* Option 2: Age threshold (1, 2, 3, 4, 5+ days) */}
                <div className="reader-form-group" style={{ marginTop: '10px' }}>
                  <label className="reader-setting-sublabel">
                    Delete articles older than:
                  </label>
                  <div className="reader-days-preset-row">
                    {[1, 2, 3, 4, 5, 7, 14, 30].map((days) => (
                      <button
                        key={days}
                        type="button"
                        className={`reader-day-pill ${autoDeleteSettings.olderThanDays === days ? 'active' : ''}`}
                        onClick={() => updateSettings({ olderThanDays: days })}
                      >
                        {days} {days === 1 ? 'day' : 'days'}
                      </button>
                    ))}
                  </div>

                  {/* Custom Days Input */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--reader-text-muted)' }}>Or custom days:</span>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      className="reader-custom-days-input"
                      value={autoDeleteSettings.olderThanDays}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 1) {
                          updateSettings({ olderThanDays: val });
                        }
                      }}
                      aria-label="Custom days threshold"
                    />
                    <span style={{ fontSize: '0.8rem', color: 'var(--reader-text-muted)' }}>days old</span>
                  </div>
                  {/* Option 3: Clean up articles from deleted feeds */}
                  <div className="reader-setting-toggle-row" style={{ marginTop: '12px' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.84rem', color: 'var(--reader-text)' }}>
                        Remove articles from deleted/unsubscribed feeds
                      </div>
                      <div style={{ fontSize: '0.76rem', color: 'var(--reader-text-muted)' }}>
                        Purges old lingering articles from feeds you are no longer subscribed to
                      </div>
                    </div>
                    <label className="reader-switch" aria-label="Toggle clean orphaned feed articles">
                      <input
                        type="checkbox"
                        checked={autoDeleteSettings.cleanOrphaned !== false}
                        onChange={(e) =>
                          updateSettings({
                            cleanOrphaned: e.target.checked,
                          })
                        }
                      />
                      <span className="reader-switch-slider" />
                    </label>
                  </div>
                </div>

                {/* Summary & Manual Cleanup Trigger */}
                <div className="reader-autodelete-footer-row">
                  <div className="reader-autodelete-summary">
                    <i className="fi fi-rr-info" style={{ color: 'var(--reader-accent)' }} />
                    <span>
                      Articles older than <strong>{autoDeleteSettings.olderThanDays} {autoDeleteSettings.olderThanDays === 1 ? 'day' : 'days'}</strong> ({autoDeleteSettings.target === 'unread' ? 'unopened only' : 'all articles even read'}{autoDeleteSettings.cleanOrphaned !== false ? ' + deleted feeds' : ''}) will be deleted from your database. Favorites are always preserved.
                    </span>
                  </div>

                  {onRunAutoDeleteNow && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '6px' }}>
                      <button
                        type="button"
                        className="reader-btn"
                        onClick={handleRunNow}
                        disabled={isCleaning}
                        title="Run auto-delete right now on current library"
                      >
                        <i className={`fi fi-rr-broom ${isCleaning ? 'reader-spin' : ''}`} style={{ fontSize: '0.78rem' }} />
                        <span>{isCleaning ? 'Cleaning…' : 'Run Auto-Delete Now'}</span>
                      </button>
                    </div>
                  )}

                  {cleanStatus && (
                    <div
                      style={{
                        padding: '6px 10px',
                        background: 'var(--reader-hover)',
                        border: '1px solid var(--reader-border)',
                        borderRadius: '4px',
                        fontSize: '0.78rem',
                        color: 'var(--reader-text)',
                        marginTop: '6px',
                      }}
                    >
                      {cleanStatus}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--reader-border)', margin: '4px 0' }} />

          {/* ========================================================= */}
          {/* 2. OPML Portability */}
          {/* ========================================================= */}
          <div>
            <h4 style={{ margin: '0 0 8px', fontSize: '0.95rem', fontWeight: 600 }}>
              OPML Feed Portability
            </h4>
            <p style={{ margin: '0 0 12px', fontSize: '0.82rem', color: 'var(--reader-text-muted)' }}>
              Easily migrate your subscriptions between Inoreader, Feedly, NetNewsWire, Readwise, and Harivishnu Reader.
            </p>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button className="reader-btn" onClick={onExportOpml} title="Export feeds to OPML">
                <i className="fi fi-rr-download" style={{ fontSize: '0.8rem' }} />
                <span>Export OPML</span>
              </button>

              <button
                className="reader-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={isImporting}
                title="Import feeds from OPML"
              >
                <i className="fi fi-rr-upload" style={{ fontSize: '0.8rem' }} />
                <span>Import OPML</span>
              </button>

              <input
                type="file"
                ref={fileInputRef}
                accept=".opml,.xml"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </div>

            {importStatus && (
              <div
                style={{
                  marginTop: '10px',
                  padding: '8px 12px',
                  background: 'var(--reader-hover)',
                  border: '1px solid var(--reader-border)',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                  color: 'var(--reader-text)',
                }}
              >
                {importStatus}
              </div>
            )}
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--reader-border)', margin: '4px 0' }} />

          {/* ========================================================= */}
          {/* 3. Keyboard Shortcuts */}
          {/* ========================================================= */}
          <div>
            <h4 style={{ margin: '0 0 12px', fontSize: '0.95rem', fontWeight: 600 }}>
              Keyboard Shortcuts
            </h4>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '8px',
                fontSize: '0.82rem',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--reader-hover)', borderRadius: '3px' }}>
                <span style={{ color: 'var(--reader-text-muted)' }}>Focus Search</span>
                <kbd style={{ fontFamily: 'var(--reader-font-mono)', fontWeight: 600 }}>/</kbd>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--reader-hover)', borderRadius: '3px' }}>
                <span style={{ color: 'var(--reader-text-muted)' }}>Next Item</span>
                <kbd style={{ fontFamily: 'var(--reader-font-mono)', fontWeight: 600 }}>j</kbd>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--reader-hover)', borderRadius: '3px' }}>
                <span style={{ color: 'var(--reader-text-muted)' }}>Previous Item</span>
                <kbd style={{ fontFamily: 'var(--reader-font-mono)', fontWeight: 600 }}>k</kbd>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--reader-hover)', borderRadius: '3px' }}>
                <span style={{ color: 'var(--reader-text-muted)' }}>Toggle Read/Unread</span>
                <kbd style={{ fontFamily: 'var(--reader-font-mono)', fontWeight: 600 }}>m</kbd>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--reader-hover)', borderRadius: '3px' }}>
                <span style={{ color: 'var(--reader-text-muted)' }}>Toggle Favorite</span>
                <kbd style={{ fontFamily: 'var(--reader-font-mono)', fontWeight: 600 }}>s</kbd>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--reader-hover)', borderRadius: '3px' }}>
                <span style={{ color: 'var(--reader-text-muted)' }}>Archive Item</span>
                <kbd style={{ fontFamily: 'var(--reader-font-mono)', fontWeight: 600 }}>a</kbd>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--reader-hover)', borderRadius: '3px' }}>
                <span style={{ color: 'var(--reader-text-muted)' }}>Open Original</span>
                <kbd style={{ fontFamily: 'var(--reader-font-mono)', fontWeight: 600 }}>v</kbd>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--reader-hover)', borderRadius: '3px' }}>
                <span style={{ color: 'var(--reader-text-muted)' }}>Close Pane / Modal</span>
                <kbd style={{ fontFamily: 'var(--reader-font-mono)', fontWeight: 600 }}>Esc</kbd>
              </div>
            </div>
          </div>
        </div>

        <div className="reader-modal-footer">
          <button type="button" className="reader-btn reader-btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
